import { useChatStore } from "@lib/stores/chatStore";
import { useUIStore } from "@lib/stores/uiStore";
import type { Organisation, User } from "@lib/types";
import { HelpCircle, MessageCircle, Monitor, Moon, Settings, Sun } from "lucide-react";
import { Link } from "react-router";
import { ChangelogButton } from "../changelog/ChangelogButton";
import { RarionMark } from "../shared/RarionMark";
import { BackgroundTasksIndicator } from "./BackgroundTasksIndicator";
import { Breadcrumb } from "./Breadcrumb";
import { NotificationBell } from "./NotificationBell";
import { OrgSwitcher } from "./OrgSwitcher";

interface TopBarProps {
  currentUser: User;
  currentOrganisation: Organisation | null;
}

function UserInitials({ user }: { user: User }) {
  const initials = (
    `${user.firstName?.[0] ?? ""}${user.lastName?.[0] ?? ""}` || user.email[0]
  ).toUpperCase();
  return (
    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-accent text-xs font-medium text-white">
      {initials}
    </div>
  );
}

const themeOrder = ["system", "light", "dark"] as const;
const themeIcons = { system: Monitor, light: Sun, dark: Moon };
const themeLabels = { system: "System theme", light: "Light theme", dark: "Dark theme" };

function ThemeToggle() {
  const themePreference = useUIStore((s) => s.themePreference);
  const setThemePreference = useUIStore((s) => s.setThemePreference);

  const nextIndex = (themeOrder.indexOf(themePreference) + 1) % themeOrder.length;
  const next = themeOrder[nextIndex];
  const Icon = themeIcons[themePreference];

  return (
    <button
      type="button"
      onClick={() => setThemePreference(next)}
      className="rounded-md p-1.5 text-text-muted transition-colors hover:bg-card-hover hover:text-text-secondary"
      title={themeLabels[themePreference]}
    >
      <Icon size={18} />
    </button>
  );
}

function ChatToggle() {
  const toggle = useChatStore((s) => s.toggle);

  return (
    <button
      type="button"
      onClick={toggle}
      className="rounded-md p-1.5 text-text-muted transition-colors hover:bg-card-hover hover:text-text-secondary"
      title="Toggle chat (⌘K)"
    >
      <MessageCircle size={18} />
    </button>
  );
}

function HomeLogo() {
  return (
    <Link
      to="/"
      className="flex items-center gap-2 text-text-primary hover:text-link transition-colors duration-150"
      title="Dashboard"
    >
      <RarionMark className="h-6 w-6 shrink-0" />
      <span className="text-base font-semibold">Rarion</span>
    </Link>
  );
}

export function TopBar({ currentUser, currentOrganisation }: TopBarProps) {
  return (
    <header className="flex h-14 items-center justify-between gap-4 border-b border-border bg-card px-6">
      <div className="flex min-w-0 items-center gap-4">
        <HomeLogo />
        <span className="h-5 w-px bg-border" aria-hidden="true" />
        <div className="min-w-0 flex-1 truncate">
          <Breadcrumb />
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        <OrgSwitcher currentOrganisation={currentOrganisation} />
        <BackgroundTasksIndicator />
        <ChatToggle />
        <NotificationBell />
        <ChangelogButton />
        <ThemeToggle />
        <Link
          to="/help"
          className="rounded-md p-1.5 text-text-muted transition-colors hover:bg-card-hover hover:text-text-secondary"
          title="Guide"
        >
          <HelpCircle size={18} />
        </Link>
        <Link
          to="/settings"
          className="rounded-md p-1.5 text-text-muted transition-colors hover:bg-card-hover hover:text-text-secondary"
          title="Organisation settings"
        >
          <Settings size={18} />
        </Link>
        <UserInitials user={currentUser} />
        <span className="text-sm text-text-secondary">
          {currentUser.fullName.trim() || currentUser.email}
        </span>
        <a
          href="/users/sign_out"
          data-method="delete"
          className="text-sm text-text-faint hover:text-text-secondary"
          onClick={(e) => {
            e.preventDefault();
            const form = document.createElement("form");
            form.method = "POST";
            form.action = "/users/sign_out";
            const methodInput = document.createElement("input");
            methodInput.type = "hidden";
            methodInput.name = "_method";
            methodInput.value = "delete";
            form.appendChild(methodInput);
            const csrfMeta = document.querySelector('meta[name="csrf-token"]');
            if (csrfMeta) {
              const csrfInput = document.createElement("input");
              csrfInput.type = "hidden";
              csrfInput.name = "authenticity_token";
              csrfInput.value = csrfMeta.getAttribute("content") || "";
              form.appendChild(csrfInput);
            }
            document.body.appendChild(form);
            form.submit();
          }}
        >
          Sign out
        </a>
      </div>
    </header>
  );
}
